"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  WithdrawalConsentCheckboxes,
  INITIAL_WITHDRAWAL_CONSENT_STATE,
  isWithdrawalConsentComplete,
  type WithdrawalConsentState,
} from "@/components/legal/withdrawal-consent-checkboxes";

/**
 * Startet den Stripe-Checkout fuer ein Abo (backlog.md -> "Monetarisierung" -> "Einzig
 * noch offen", Feedback-Runde 19 Punkt 21): zeigt vor der Weiterleitung zu Stripe die
 * gesetzlich vorgeschriebene Widerrufs-Doppel-Bestaetigung (§ 356 Abs. 6 BGB) -
 * "Weiter zu Stripe" bleibt deaktiviert, bis beide Checkboxen angehakt sind. Reiner
 * Code-Vorlauf: aufrufende Stellen (PricingBundle) uebergeben weiterhin ihr eigenes
 * `disabled` (an PAYMENTS_ENABLED gekoppelt) - erst wenn Erik den Schalter umlegt UND
 * ein echter Stripe-Account existiert, wird dieser Button tatsaechlich klickbar/
 * funktionsfaehig.
 */
export function UpgradeCheckoutButton({
  tier,
  period = "monthly",
  label,
  disabled = false,
  className,
}: {
  tier: "pro" | "max";
  period?: "monthly" | "yearly";
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [consent, setConsent] = useState<WithdrawalConsentState>(INITIAL_WITHDRAWAL_CONSENT_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startCheckout() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier, period }),
      });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        setError(
          res.status === 401
            ? "Bitte zuerst einloggen, um ein Abo abzuschließen."
            : (data.error ?? "Checkout konnte nicht gestartet werden."),
        );
        setLoading(false);
        return;
      }
      window.location.href = data.url;
    } catch {
      setError("Checkout konnte nicht gestartet werden.");
      setLoading(false);
    }
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      // Zustand zuruecksetzen, damit ein erneutes Oeffnen nicht mit bereits
      // angehakten Checkboxen oder einer alten Fehlermeldung startet.
      setConsent(INITIAL_WITHDRAWAL_CONSENT_STATE);
      setError(null);
      setLoading(false);
    }
  }

  return (
    <>
      <Button type="button" size="sm" disabled={disabled} className={className} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Bevor es weitergeht</DialogTitle>
          </DialogHeader>
          <WithdrawalConsentCheckboxes state={consent} onChange={setConsent} />
          {error && <p className="text-sm text-negative">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              disabled={!isWithdrawalConsentComplete(consent) || loading}
              onClick={startCheckout}
            >
              {loading ? "Wird geöffnet…" : "Weiter zu Stripe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
